import React, { PropsWithChildren, useMemo } from 'react'
import { StyleSheet, ViewStyle } from 'react-native'

import {
  DNABox,
  DNAButton,
  DNAText,
  DNAContextMenu,
  Stack,
  DNAIcon,
  Iffy,
} from '@alucio/lux-ui'

import {
  Page,
} from '@alucio/aws-beacon-amplify/src/models';
import {
  PayloadGroup,
  usePresentationBuilderState,
  GroupStatus,
  ModifiedPayloadGroup,
  unavailableStatus,
} from 'src/components/PresentationBuilder/state/PresentationBuilderStateProvider'
import { MagnifyConfig } from 'src/components/DNA/Thumbnail/DNAThumbnail'

const S = StyleSheet.create({
  opaqueBackground: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  topRightIconButton: {
    marginTop: 4,
    marginRight: 4,
  },
  topRightIconButtonWithThreeDots: {
    marginTop: 32,
    marginRight: 4,
  },
  containerMargin: {
    margin: 8,
  },
})

export const isPresentationBuilderGroupHidden = (group: ModifiedPayloadGroup): boolean => {
  if (!group.visible) {
    return true;
  }

  return group.pages.length === 1 && (
    group.pages[0].isHidden === true ||
    (!!group.pages[0].sourceIsHidden && typeof group.pages[0].isHidden !== 'boolean')
  );
};

export const GroupItemContextMenu: React.FC<{
  group: ModifiedPayloadGroup,
  page?: Page,
}> = (props) => {
  const { group, page } = props
  const {
    toggleHiddenPage,
    toggleGroupVisibility,
    canRemoveGroup,
    isSingleAndHasAssociation,
    onRemoveGroup,
  } = usePresentationBuilderState()

  const onHide = () => {
    const singlePage = page || (group.pages.length === 1 ? group.pages[0] : undefined);

    if (singlePage?.pageId) {
      toggleHiddenPage(group.id, singlePage.pageId);
      return;
    }

    toggleGroupVisibility(group.id);
  }

  const isHidden = page
    ? page.isHidden === true || (page.sourceIsHidden && typeof page.isHidden !== 'boolean')
    : isPresentationBuilderGroupHidden(group);

  const canRemove = canRemoveGroup(group)
  const hasAssociatedSlide = isSingleAndHasAssociation(group)

  return (
    <DNAContextMenu>
      <DNAContextMenu.Anchor>
        <DNAButton
          status="transparent"
          iconLeft="dots-three-vertical-bold"
          // @ts-ignore - onMouseDown prevents the sortable parent from starting a drag on web
          onMouseDown={(event: React.MouseEvent) => event.stopPropagation()}
          style={S.topRightIconButton}
        />
      </DNAContextMenu.Anchor>
      <DNAContextMenu.Items>
        <DNAContextMenu.Item
          title={isHidden ? 'Unhide' : 'Hide'}
          icon={isHidden ? 'eye' : 'eye-slash'}
          onPress={onHide}
        />
        {
          canRemove && !hasAssociatedSlide &&
            <DNAContextMenu.Item
              title="Remove"
              icon="trash"
              onPress={() => onRemoveGroup(group)}
              status="danger"
              iconStatus="danger"
            />
        }
      </DNAContextMenu.Items>
    </DNAContextMenu>
  )
}

export const FindAndReplaceOverlay: React.FC<{ group: PayloadGroup }> = (props) => {
  const { group } = props
  const { isLocked, onRemoveGroup } = usePresentationBuilderState()

  return (
    <DNABox
      fill
      appearance="col"
      style={S.containerMargin}
    >
      <DNABox alignX="end">
        <Iffy is={!isLocked}>
          <DNAButton
            status="transparent"
            iconLeft="trash"
            onPress={() => onRemoveGroup(group)}
            style={S.topRightIconButton}
          />
        </Iffy>
      </DNABox>
      <Iffy is={isLocked}>
        <DNABox fill alignX="center" alignY="center">
          <DNAText bold b1 colorMode="dark">
            Source file has been recently updated
          </DNAText>
        </DNABox>
      </Iffy>
    </DNABox>
  )
}

interface GroupItemOverlayProps {
  style?: ViewStyle,
  group: ModifiedPayloadGroup,
  page?: Page,
  enabled?: boolean,
  isLocked?: boolean,
  magnifyConfig?: MagnifyConfig,
}

export const GroupItemOverlay: React.FC<PropsWithChildren<GroupItemOverlayProps>> = (
  { group, page, style, enabled, children, isLocked, magnifyConfig },
) => {
  const isUnavailable = !!unavailableStatus[group.groupStatus]
  const isFindAndReplace = group.groupStatus === GroupStatus.MAJOR_UPDATE
  const hideContextMenu = isFindAndReplace || isUnavailable
  const isSingleSlide = group.pages.length === 1
  const hasHiddenPage = page
    ? page.isHidden === true || (page.sourceIsHidden && typeof page.isHidden !== 'boolean')
    : isSingleSlide && group.pages.some(groupPage => (
      groupPage.isHidden === true ||
      (groupPage.sourceIsHidden && typeof groupPage.isHidden !== 'boolean')
    ))
  const finalStyle = StyleSheet.flatten([
    { padding: 4 },
    style,
  ])

  const ellipsisStyle = useMemo(
    () => magnifyConfig?.enabled ? S.topRightIconButtonWithThreeDots : S.topRightIconButton,
    [magnifyConfig?.enabled],
  )

  if (!enabled)
  { return children as React.ReactElement }

  const applyOpaqueBackground = !group.visible || hasHiddenPage ||
    (isFindAndReplace && isSingleSlide && isLocked)

  return (
    <DNABox fill style={finalStyle}>
      <Stack>
        {/* Children - Base Layer */}
        <Stack.Layer>
          { children }
        </Stack.Layer>

        {/* Opaque background for multiple scenarios */}
        {
          applyOpaqueBackground &&
            <Stack.Layer fill style={{ pointerEvents: 'none', margin: 6 }}>
              <DNABox fill alignX="center" alignY="center" style={S.opaqueBackground}/>
            </Stack.Layer>
        }

        {/* Hidden Group Overlay */}
        {
          (!group.visible || hasHiddenPage) &&
            <Stack.Layer fill style={{ pointerEvents: 'none' }}>
              <DNABox fill alignX="center" alignY="center">
                <DNAIcon.Styled name="eye-slash" colorMode="dark" status="subtle" appearance="ghost" />
              </DNABox>
            </Stack.Layer>
        }

        {/* Find & Replace Overlay */}
        {
          isFindAndReplace && isSingleSlide &&
            <Stack.Layer fill>
              <FindAndReplaceOverlay group={group}/>
            </Stack.Layer>
        }

        {/* Context Menu */}
        {
          !hideContextMenu && !isLocked && (isSingleSlide || !!page) &&
            <Stack.Layer anchor="topRight" style={ellipsisStyle}>
              <DNABox style={S.containerMargin}>
                <GroupItemContextMenu group={group} page={page}/>
              </DNABox>
            </Stack.Layer>
        }
      </Stack>
    </DNABox>
  )
}
